export const translations = {
  en: {
    // Navigation
    nav: {
      games: 'Games',
      newGame: 'New Game',
      analytics: 'Analytics',
      aiTrainer: 'AI Trainer',
      feedback: 'Feedback',
      profile: 'Profile',
      signOut: 'Sign Out',
      upgradeToPro: 'Upgrade to Pro',
      coach: 'Coach',
      featureRequests: 'Feature Requests'
    },

    // Common
    common: {
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      loading: 'Loading...',
      deleting: 'Deleting...',
      error: 'Error',
      success: 'Success',
      yes: 'Yes',
      no: 'No',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      finish: 'Finish',
      upload: 'Upload',
      analyze: 'Analyze',
      analyzing: 'Analyzing...',
      processing: 'Processing...',
      view: 'View',
      download: 'Download',
      share: 'Share',
      name: 'Name',
      loadMoreSessions: 'Load More Sessions',
      futureDatesMessage: 'You can select future dates to prepare game sheets in advance',
      loadingYourGames: 'Loading your games...',
      actions: 'Actions',
      notRecorded: 'Not recorded'
    },

    // Notifications and Messages
    notifications: {
      videoAnalysisComplete: 'Video Analysis complete - {count} goals generated with goalie analysis',
      videoNotAvailable: 'Video not available',
      videoPlayerNotReady: 'Video player not ready, please try again',
      failedToLoadProfile: 'Failed to load profile',
      failedToUpdateProfile: 'Failed to update profile',
      securityVerificationFailed: 'Security verification failed. Please try again.',
      failedToCreateAccount: 'Failed to create account. Please try again.',
      unexpectedError: 'An unexpected error occurred. Please try again.',
      fileSizeTooLarge: 'File size too large. Maximum size is {size}MB',
      unsupportedFileType: 'Unsupported file type. Please upload MP4, MOV, AVI, WebM, or MKV files',
      invalidVideoUrl: 'Please provide a direct video file URL (must end with .mp4, .mov, .avi, .webm, or .mkv)',
      platformUrlNotSupported: 'Platform video URLs are not supported. Please use a direct video file URL',
      parseVideoAnalysisFailed: 'Failed to parse video analysis results. Please try again.',
      analysisUnavailable: 'Unable to generate analysis at this time. Please try again later.',
      dropImageHere: 'Drop the image here',
      dragDropImage: 'Drag & drop an image here, or click to select',
      maxFileSize5MB: 'Maximum file size: 5MB',
      maxFileSize50MB: 'Maximum file size: 50MB',
      liveCount: 'Live count'
    },

    // Game Details
    game: {
      title: 'Game Details',
      opponent: 'Opponent',
      date: 'Date',
      competitionType: 'Competition Type',
      shotsFaced: 'Shots Faced',
      saves: 'Saves',
      goalsAllowed: 'Goals Allowed',
      savePercentage: 'Save %',
      outcome: 'Outcome',
      addGoal: 'Add Goal',
      editGame: 'Edit Game',
      deleteGame: 'Delete Game',
      noGoals: 'No goals recorded for this game.',
      addFirstGoal: 'Add Your First Goal',
      
      // Competition types
      competitionTypes: {
        'regular-season': 'Regular Season',
        'playoff': 'Playoff',
        'tournament': 'Tournament',
        'exhibition': 'Exhibition',
        'practice': 'Practice'
      },

      // Outcomes
      outcomes: {
        'win': 'WIN',
        'loss': 'LOSS',
        'win-ot': 'WIN OT',
        'loss-ot': 'LOSS OT',
        'win-so': 'WIN SO',
        'loss-so': 'LOSS SO'
      },
      
      // Validation and errors
      validation: {
        pleaseEnterValidDate: 'Please enter a valid date',
        pleaseProvideVideoUrl: 'Please provide a video URL or upload a video file',
        failedToFetchVideo: 'Failed to fetch video',
        failedToStartUpgrade: 'Failed to start upgrade process',
        gameNotFound: 'Game not found'
      },
      
      // Game details labels
      labels: {
        saveChanges: 'Save Changes',
        period1: 'Period 1:',
        period2: 'Period 2:',
        period3: 'Period 3:',
        overtime: 'Overtime:',
        uploadVideoFile: 'Upload Video File',
        submittingToAI: 'Submitting to AI...',
        shotLocation: 'Shot Location',
        goalLocation: 'Goal Location',
        aiGoalieAnalysis: 'AI Goalie Analysis',
        goalieTechnique: 'Goalie Technique',
        videoUrlRequirement: 'Must be a direct video file URL (.mp4, .mov, .avi, .webm, .mkv)',
        aiPrefilledMessage: 'AI has pre-filled the shot and goal locations. You can adjust them by clicking on the diagrams below.',
        situationDescription: 'Situation Description',
        videoUrl: 'Video URL',
        url: 'URL',
        upload: 'Upload'
      }
    },

    // Live Shots Counter
    liveShotsCounter: {
      title: 'Live count',
      period1: 'Period 1',
      period2: 'Period 2', 
      period3: 'Period 3',
      overtime: 'Overtime',
      totalShots: 'Total Shots',
      cancel: 'Cancel',
      saveShots: 'Save Shots'
    },

    // Feedback List
    feedbackList: {
      noFeedbackYet: 'No feedback yet',
      unknownRecipient: 'Unknown Recipient',
      suggestedDrills: 'Suggested Drills',
      watchVideo: 'Watch Video',
      exerciseDrawing: 'Exercise Drawing',
      viewDetails: 'View Details',
      deleteFeedback: 'Delete Feedback',
      deleteFeedbackTitle: 'Delete Feedback',
      deleteFeedbackConfirmation: 'Are you sure you want to delete this feedback? This action cannot be undone.',
      cancel: 'Cancel'
    },

    // Saves List
    savesList: {
      title: 'Saves & Good Things',
      addSave: 'Add Save',
      description: 'Add what you did great during the game - highlight your best saves and memorable moments.',
      titleLabel: 'Title',
      titlePlaceholder: 'e.g., Great Glove Save',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Describe the save or situation...',
      cancel: 'Cancel',
      adding: 'Adding...',
      addSaveButton: 'Add Save',
      noSavesYet: 'No saves recorded yet',
      deleteSave: 'Delete Save',
      deleteSaveTitle: 'Delete Save',
      deleteSaveConfirmation: 'Are you sure you want to delete this save? This action cannot be undone.'
    },

    // Shot Analysis Card
    shotAnalysisCard: {
      technique: 'Technique:',
      viewVideoClip: 'View Video Clip',
      whatWentWell: 'What Went Well',
      areasForImprovement: 'Areas for Improvement',
      keyMoment: 'Key Moment',
      coachingTip: 'Coaching Tip',
      shotVisualization: 'Shot Visualization',
      shotOriginPosition: 'Shot Origin Position',
      shotDestinationOnGoal: 'Shot Destination on Goal'
    },

    // Feedback Form
    feedbackForm: {
      feedbackPlaceholder: 'Share your thoughts, suggestions, or observations...',
      suggestedDrills: 'Suggested Drills',
      addDrill: 'Add Drill',
      drillTitle: 'Drill Title',
      drillTitlePlaceholder: 'e.g., Lateral Movement Drill',
      description: 'Description',
      descriptionPlaceholder: 'Describe the drill and how to perform it...',
      videoUrlOptional: 'Video URL (optional)',
      videoUrlPlaceholder: 'https://...'
    },

    // Goal Form
    goalForm: {
      title: 'Add Goal',
      editTitle: 'Edit Goal',
      goalTitle: 'Goal Title',
      shotType: 'Shot Type',
      gameSituation: 'Game Situation',
      situationDescription: 'Situation Description',
      clipTiming: 'Clip Timing',
      startTime: 'Start Time',
      endTime: 'End Time',
      goalPosition: 'Goal Position',
      shotPosition: 'Shot Position',
      videoAnalysis: 'AI Video Analysis',
      analyzeVideo: 'Analyze Video',
      uploadVideo: 'Upload Video',
      videoUrl: 'Video URL',
      analyzing: 'Analyzing video...',
      uploading: 'Uploading video...',
      
      // Shot types
      shotTypes: {
        'Wrist Shot': 'Wrist Shot',
        'Slap Shot': 'Slap Shot',
        'Snap Shot': 'Snap Shot',
        'Backhand': 'Backhand',
        'Deflection / Tip-in': 'Deflection / Tip-in',
        'One-Timer': 'One-Timer',
        'Wraparound': 'Wraparound',
        'Rebound Shot': 'Rebound Shot'
      },

      // Game situations
      gameSituations: {
        'Break away': 'Break away',
        'Penalty Shot': 'Penalty Shot',
        'Screened Shot': 'Screened Shot',
        'Odd-Man Rush': 'Odd-Man Rush',
        'Cross-Ice Pass': 'Cross-Ice Pass',
        'Point Shot': 'Point Shot',
        'Net-Front Battle': 'Net-Front Battle',
        'Shorthanded Goal': 'Shorthanded Goal',
        'Power Play Goal': 'Power Play Goal',
        'Turnover Leading to Goal': 'Turnover Leading to Goal',
        'Off the Rush': 'Off the Rush',
        'Puck Battle / Scramble': 'Puck Battle / Scramble'
      },

      validation: {
        titleRequired: 'Goal title is required',
        shotTypeRequired: 'Shot type is required',
        gameSituationRequired: 'Game situation is required',
        positionRequired: 'Please click on the goal diagram to set the goal position',
        shotPositionRequired: 'Please click on the shot diagram to set the shot position'
      },

      aiAnalysis: {
        preFilled: 'AI has pre-filled the shot location. You can adjust it by clicking on the diagram.',
        technique: 'Goalie Technique',
        whatWentWell: 'What Went Well',
        areasForImprovement: 'Areas for Improvement',
        keyMoment: 'Key Moment',
        coachingTip: 'Coaching Tip'
      }
    },

    // Video Player
    videoPlayer: {
      showPlayer: 'Show Player',
      hidePlayer: 'Hide Player',
      viewClip: 'View Clip'
    },

    // AI Trainer
    aiTrainer: {
      title: 'AI Trainer',
      subtitle: 'Your personalized goalie coaching sessions powered by AI',
      analyzeWithAI: 'Analyze with AI Trainer',
      analyzing: 'Analyzing...',
      sessions: 'AI Training Sessions',
      noSessions: 'No AI training sessions yet.',
      createFirst: 'Create your first analysis to get personalized coaching insights.',
      limitReached: 'AI Analysis Limit Reached',
      limitMessage: 'You\'ve used {used} of {limit} free analyses. Upgrade to continue.',
      upgradeRequired: 'You have reached your free AI analysis limit. Please upgrade to continue.',
      
      // Analysis sections
      analysis: {
        title: 'Performance Analysis',
        drills: 'Recommended Drills',
        tips: 'Quick Tips',
        motivation: 'Motivational Note'
      }
    },

    // Dashboard
    dashboard: {
      title: 'Games Dashboard',
      recentGames: 'Recent Games',
      quickStats: 'Quick Stats',
      totalGames: 'Total Games',
      totalSaves: 'Total Saves',
      avgSavePercentage: 'Avg Save %',
      noGames: 'No games recorded yet.',
      createFirst: 'Create your first game to start tracking your performance.',
      trackPerformance: 'Track your performance and analyze your progress',
      newFeatures: 'New Features!',
      recordNewGame: 'Record New Game',
      averageSavePercentage: 'Average Save %',
      date: 'Date',
      opponent: 'Opponent',
      competition: 'Competition',
      savePercentage: 'Save %',
      goals: 'Goals',
      actions: 'Actions'
    },

    // Analytics
    analytics: {
      title: 'Analytics',
      subtitle: 'Analyze your performance patterns and trends',
      overview: 'Overview',
      trends: 'Trends',
      breakdown: 'Breakdown',
      allShotTypesDistribution: 'All Shot Types Distribution',
      allGameSituationsDistribution: 'All Game Situations Distribution',
      selectGames: 'Select Games',
      selectAll: 'Select All',
      clearSelection: 'Clear Selection',
      selectedGames: 'Selected Games',
      gameOutcomesBreakdown: 'Game Outcomes Breakdown',
      top5ShotTypes: 'Top 5 Shot Types',
      allShotTypes: 'All Shot Types',
      top5GameSituations: 'Top 5 Game Situations',
      allGameSituations: 'All Game Situations',
      showAll: 'Show All',
      showTop5: 'Show Top 5',
      noGamesSelected: 'No games selected. Please select at least one game to view analytics.',
      selectGamesToAnalyze: 'Select games to analyze',
      clickToToggle: 'Click to toggle selection',
      exportPDF: 'Export PDF',
      shareAnalytics: 'Share Analytics',
      upgradeToUnlock: 'Upgrade to unlock this feature',
      premiumFeature: 'Premium Feature',
      dateRange: 'Date Range',
      from: 'From',
      to: 'To',
      all: 'All',
      single: 'Single',
      multiple: 'Multiple',
      range: 'Range',
      seeAllShotTypes: 'See All Shot Types',
      seeAllGameSituations: 'See All Game Situations',
      top5ShotTypesDistribution: 'Top 5 Shot Types Distribution',
      top5GameSituationsDistribution: 'Top 5 Game Situations Distribution',
      analyticsResults: 'Analytics Results',
      detailedVisualization: 'Detailed visualization of your performance data',
      goalDistributionHeatmap: 'Goal Distribution Heatmap',
      heatmapDescription: 'The heatmap shows the percentage of goals scored in each zone.',
      shotPositionAnalysis: 'Shot Position Analysis',
      winPercentage: 'Win Percentage',
      importantNote: 'Important Note',
      accurateStatisticsNote: 'For accurate statistics, you need to add goals on the diagram for each game. Statistics are calculated based on the goals you\'ve recorded, not just the game results.',
      totalGoalsAgainst: 'Total Goals Against',
      acrossGames: 'Across {count} games',
      averageGoalsPerGame: 'Average Goals per Game',
      basedOnSelectedData: 'Based on selected data',
      savePercentage: 'Save Percentage',
      overallSavePercentage: 'Overall save percentage',
      basedOnGamesWithOutcomes: 'Based on games with outcomes',
      acrossSelectedGames: 'Across {count} selected game{plural}',
      goalLocations: 'Goal Locations',
      goalPointsExplanation: 'Each point represents a goal\'s location',
      upgradeForAIAnalysis: 'Upgrade for AI Analysis',
      shotDistributionHeatmap: 'Shot Distribution Heatmap',
      shotLocations: 'Shot Locations',
      shotHeatmapDescription: 'The heatmap shows the percentage of shots taken from each zone. Darker red indicates a higher percentage of shots.',
      shotPointsDescription: 'Each point represents where a shot was taken from.',
      seekingTo: 'Seeking to',
      
      // Risk Analysis
      riskAnalysis: 'Risk Analysis',
      riskAnalysisCaption: 'Key performance insights based on your goal and save data',
      addMoreGoalsToSeeAnalysis: 'Add more goals to see analysis',
      topStrength: 'Top Strength',
      topWeakness: 'Top Weakness',
      mostRiskySituation: 'Most Risky Situation',
      mostRiskyShotType: 'Most Risky Shot Type'
    },

    // AI Trainer Session
    aiTrainerSession: {
      title: 'AI Trainer Session',
      backToAITrainer: 'Back to AI Trainer',
      sessionNotFound: 'Session not found',
      permissionDenied: 'You do not have permission to view this session',
      loadingSession: 'Loading AI training session...',
      failedToLoad: 'Failed to load AI trainer session',
      gamesAnalyzed: 'Games analyzed',
      analyzed: 'analyzed',
      aiCoachAnalysis: 'AI Coach Analysis',
      recommendedDrills: 'Recommended Drills',
      performanceTips: 'Performance Tips',
      motivationalNote: 'Motivational Note',
      goalDistribution: 'Goal Distribution',
      goalLocations: 'Goal Locations',
      shotDistribution: 'Shot Distribution',
      shotLocations: 'Shot Locations',
      sessionStats: 'Session Stats',
      totalGoals: 'Total Goals',
      drillsRecommended: 'Drills Recommended',
      tipsProvided: 'Tips Provided',
      watchVideo: 'Watch Video',
      vs: 'vs'
    },

    // Admin Panel
    adminPanel: {
      title: 'Admin Panel',
      signInToManage: 'Sign in to manage system settings',
      username: 'Username',
      password: 'Password',
      signingIn: 'Signing in...',
      signIn: 'Sign In',
      systemSettings: 'System Settings',
      logout: 'Logout',
      freeGamesLimit: 'Free Games Limit',
      freeGamesDescription: 'Number of games users can create for free',
      freeAIAnalysisLimit: 'Free AI Analysis Limit',
      freeAnalysisDescription: 'Number of AI analyses users can generate for free',
      saving: 'Saving...',
      saveSettings: 'Save Settings',
      settingsUpdatedSuccessfully: 'Settings updated successfully!',
      stripeProductsAndPricing: 'Stripe Products & Pricing',
      loadingProducts: 'Loading products...',
      currentPrice: 'Current Price',
      noActivePrices: 'No active prices',
      updatePrice: 'Update Price ($)',
      updating: 'Updating...',
      updatePriceButton: 'Update Price',
      priceUpdatedSuccessfully: 'Price updated successfully for product',
      loginFailed: 'Login failed',
      updateFailed: 'Update failed',
      pleaseEnterValidPrice: 'Please enter a valid price',
      failedToFetchProducts: 'Failed to fetch Stripe products',
      failedToUpdatePrice: 'Failed to update price'
    },

    // Coach Dashboard
    coachDashboard: {
      title: 'Coach Dashboard',
      subtitle: 'Manage your coaching activities and profile settings',
      studentManagement: 'Student Management',
      manageStudentsAndSessions: 'Manage your students and practice sessions',
      trackStudentProgress: 'Track student progress',
      planPracticeSessions: 'Plan practice sessions',
      monitorDevelopment: 'Monitor development',
      goalieSchoolProfile: 'Goalie School Profile',
      manageCoachingProfile: 'Manage your coaching business profile',
      publicCoachingProfile: 'Public coaching profile',
      servicesAndPricing: 'Services and pricing',
      contactInformation: 'Contact information',
      gettingStartedAsCoach: 'Getting Started as a Coach',
      setupProfileDescription: 'Set up your coaching profile to attract new students and use the student management tools to track their progress.',
      createGoalieSchoolProfile: 'Create your goalie school profile to be discoverable by potential students',
      addStudentsToTrack: 'Add students to track their development and plan targeted practice sessions',
      useAnalyticsTools: 'Use the analytics tools to provide detailed feedback to your students'
    },

    // Feedback
    feedback: {
      title: 'Feedback',
      hireACoach: 'Hire a Coach',
      loadingFeedback: 'Loading feedback...',
      failedToLoadFeedback: 'Failed to load feedback',
      received: 'Received',
      sent: 'Sent',
      searchFeedback: 'Search Feedback',
      searchPlaceholder: 'Search by content, author, or opponent...',
      dateRange: 'Date Range'
    },

    // Shared View
    sharedView: {
      title: 'Shared Analysis',
      sharedBy: 'Shared by',
      loadingSharedAnalysis: 'Loading shared analysis...',
      shareNotFound: 'Share not found',
      failedToLoadSharedData: 'Failed to load shared data. Please try again.',
      preparingPDF: 'Preparing PDF...',
      exportPDF: 'Export PDF',
      totalGoalsAgainst: 'Total Goals Against',
      acrossGames: 'Across {count} games',
      averageGoalsPerGame: 'Average Goals per Game',
      basedOnAllGames: 'Based on all games',
      basedOnSelectedData: 'Based on selected data',
      savePercentage: 'Save Percentage',
      overallSavePercentage: 'Overall save percentage',
      basedOnGamesWithOutcomes: 'Based on games with outcomes',
      acrossSelectedGames: 'Across {count} selected game{plural}',
      importantNote: 'Important Note',
      accurateStatisticsNote: 'For accurate statistics, you need to add goals on the diagram for each game. Statistics are calculated based on the goals you\'ve recorded, not just the game results.',
      feedback: 'Feedback',
      selectGameForFeedback: 'Select Game for Feedback',
      goalDistributionHeatmap: 'Goal Distribution Heatmap',
      shotPositionAnalysis: 'Shot Position Analysis',
      savesAndGoodThings: 'Saves & Good Things',
      highlightsAndMoments: 'Highlights and memorable moments from this game',
      goalsTimeline: 'Goals Timeline',
      noGoalsRecorded: 'No goals recorded for this game',
      shotTypesDistribution: 'Shot Types Distribution',
      allFeedback: 'All Feedback',
      goalLocations: 'Goal Locations',
      shotLocations: 'Shot Locations',
      heatmapExplanation: 'The heatmap shows the percentage of goals scored in each zone. Darker red indicates a higher percentage of goals.',
      goalPointsExplanation: 'Each point represents a goal\'s location.',
      shotHeatmapExplanation: 'The heatmap shows the percentage of shots taken from each zone.',
      shotPointsExplanation: 'Each point represents where a shot was taken from.'
    },

    // Game Details
    gameDetails: {
      upgradeForAIAnalysis: 'Upgrade for AI Analysis',
      analyzeGamePerformance: 'Analyze your game performance and track goal patterns',
      exportPDF: 'Export PDF',
      share: 'Share',
      shotsFaced: 'Shots Faced',
      shots: 'Shots',
      totalShotsFaced: 'Total shots faced',
      saves: 'Saves',
      totalSavesMade: 'Total saves made',
      goalsAgainst: 'Goals Against',
      goalsConceded: 'Goals conceded',
      aiVideoAnalysis: 'AI Video Analysis',
      betaDisclaimer: 'AI-generated goals may have inaccurate shot positions, types, and details. Please review and edit them in the goals list below.',
      analyzeVideoForGoals: 'Analyze Video for Goals',
      goalsAnalysis: 'Goals Analysis',
      goalLocation: 'Goal Location',
      shotLocation: 'Shot Location',
      goals: 'Goals',
      whatWentWell: 'What Went Well',
      areasForImprovement: 'Areas for Improvement',
      keyMoment: 'Key Moment',
      coachingTip: 'Coaching Tip',
      viewClip: 'View Clip',
      deleteGoal: 'Delete Goal',
      deleteGame: 'Delete Game',
      selectOutcome: 'Select outcome',
      goalTitlePlaceholder: 'e.g., Breakaway Goal',
      situationDescriptionPlaceholder: 'Describe the situation leading to the goal...',
      videoUrlPlaceholder: 'Enter direct video URL (e.g., https://example.com/video.mp4)',
      cancelEditing: 'Cancel editing',
      editGame: 'Edit game',
      editGoal: 'Edit goal',
      selectShotType: 'Select shot type',
      selectGameSituation: 'Select game situation',
      savesCalculatedAutomatically: 'Saves will be calculated automatically based on shots faced and goals against',
      gameOutcome: 'Game Outcome (optional)'
    },

    // Student Management
    studentManagement: {
      title: 'Student Management',
      addStudent: 'Add Student',
      editStudent: 'Edit Student',
      addNewStudent: 'Add New Student',
      updateStudent: 'Update Student',
      deleteStudent: 'Delete Student',
      loadingStudents: 'Loading students...',
      noStudentsYet: 'No students yet',
      noStudentsDescription: 'Add your first student to start tracking their progress.',
      addFirstStudent: 'Add Your First Student',
      practiceSessions: 'Practice Sessions',
      addPractice: 'Add Practice',
      updatePractice: 'Update Practice',
      noPracticeSessionsYet: 'No practice sessions recorded yet',
      noPracticeSessionsDescription: 'Start adding practice sessions to track progress.',
      firstName: 'First Name',
      lastName: 'Last Name',
      age: 'Age',
      level: 'Level',
      team: 'Team',
      strengths: 'Strengths',
      areasToImprove: 'Areas to Improve',
      notes: 'Notes',
      date: 'Date',
      duration: 'Duration (minutes)',
      focusAreas: 'Focus Areas',
      progress: 'Progress',
      drills: 'Drills',
      addDrill: 'Add Drill',
      drillTitle: 'Drill Title',
      drillDescription: 'Description',
      videoUrl: 'Video URL (optional)',
      failedToLoadStudents: 'Failed to load students and practice sessions',
      failedToSaveStudent: 'Failed to save student',
      failedToSavePractice: 'Failed to save practice session',
      failedToDeleteStudent: 'Failed to delete student',
      failedToDeletePractice: 'Failed to delete practice session',
      deleteStudentConfirmation: 'Are you sure you want to delete this student? This action cannot be undone.',
      deletePracticeConfirmation: 'Are you sure you want to delete this practice session? This action cannot be undone.',
      cancel: 'Cancel',
      save: 'Save',
      update: 'Update',
      delete: 'Delete',
      practiceMinutes: 'minutes'
    },

    // Upvotes/Feature Requests
    upvotes: {
      title: 'Feature Requests',
      subtitle: 'Vote for features you\'d like to see in GoalR or suggest new ones',
      newRequest: 'New Request',
      newFeatureRequest: 'New Feature Request',
      loadingFeatureRequests: 'Loading feature requests...',
      failedToLoadRequests: 'Failed to load feature requests',
      failedToAddRequest: 'Failed to add feature request',
      failedToUpdateVote: 'Failed to update vote',
      failedToDeleteRequest: 'Failed to delete feature request',
      requestTitle: 'Title',
      titlePlaceholder: 'What feature would you like to see?',
      description: 'Description',
      descriptionPlaceholder: 'Describe the feature and how it would help...',
      cancel: 'Cancel',
      submitRequest: 'Submit Request',
      requestedBy: 'Requested by',
      deleteFeatureRequest: 'Delete Feature Request',
      deleteConfirmation: 'Are you sure you want to delete this feature request? This action cannot be undone.',
      deleteRequest: 'Delete Request'
    },

    // Profile
    profile: {
      title: 'Profile',
      personalInfo: 'Personal Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      age: 'Age',
      level: 'Level',
      team: 'Team',
      subscription: 'Subscription',
      usage: 'Usage',
      gamesCreated: 'Games Created',
      analysesCreated: 'Analyses Created',
      upgradeDescription: 'Get access to advanced features and unlimited analysis:',
      unlimitedGameAnalysis: 'Unlimited game analysis',
      advancedAIFeatures: 'Advanced AI training features',
      prioritySupport: 'Priority support',
      upgradeNow: 'Upgrade Now'
    },

    // Authentication
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot password?',
      sending: 'Sending...',
      signingIn: 'Signing in...',
      signingUp: 'Signing up...',
      creatingAccount: 'Creating Account...',
      dontHaveAccount: 'Don\'t have an account?',
      alreadyHaveAccount: 'Already have an account?',
      resetPasswordSent: 'Password reset email sent! Please check your inbox.',
      invalidEmailPassword: 'Invalid email or password',
      enterEmailAddress: 'Please enter your email address',
      passwordResetFailed: 'Failed to send password reset email. Please check your email address.',
      createAccount: 'Create Account',
      firstName: 'First Name',
      lastName: 'Last Name',
      age: 'Age',
      level: 'Level',
      team: 'Team',
      selectLevel: 'Select a level',
      recreational: 'Recreational',
      junior: 'Junior',
      highSchool: 'High School',
      college: 'College',
      semiProfessional: 'Semi-Professional',
      professional: 'Professional'
    },

    // Errors and Messages
    messages: {
      gameNotFound: 'Game not found',
      loadingError: 'Failed to load data',
      saveError: 'Failed to save',
      deleteError: 'Failed to delete',
      uploadError: 'Failed to upload',
      analysisError: 'Failed to analyze video',
      networkError: 'Network error. Please check your connection.',
      sessionExpired: 'Your session has expired. Please sign in again.',
      unauthorizedAccess: 'You don\'t have permission to access this resource.',
      videoTooLarge: 'Video file is too large. Please choose a smaller file.',
      unsupportedFormat: 'Unsupported file format. Please use MP4, MOV, or AVI.',
      
      // Success messages
      gameSaved: 'Game saved successfully',
      goalSaved: 'Goal saved successfully',
      goalDeleted: 'Goal deleted successfully',
      gameDeleted: 'Game deleted successfully',
      videoUploaded: 'Video uploaded successfully',
      analysisComplete: 'Analysis completed successfully',
      profileNotFound: 'Profile not found',
      aiAnalyzingVideo: 'AI is analyzing your video to identify shots and saves. This may take a few moments...',
      confirmDeleteGoal: 'Are you sure you want to delete this goal? This action cannot be undone.',
      confirmDeleteGame: 'Are you sure you want to delete this game and all its associated goals? This action cannot be undone.'
    },

    // New Game
    newGame: {
      title: 'Record New Game',
      subtitle: 'Track your performance and improve your skills',
      gameDate: 'Game Date',
      opponent: 'Opponent',
      opponentPlaceholder: 'Enter opponent team name',
      competitionType: 'Competition Type',
      shotsFaced: 'Shots Faced (Optional)',
      saves: 'Saves (Optional)', 
      gameOutcome: 'Game Outcome (Optional)',
      selectOutcome: 'Select outcome',
      clearSelection: 'Clear selection',
      creatingGame: 'Creating Game...',
      createGame: 'Create Game',
      cancel: 'Cancel',
      gameLimitReached: 'Game Limit Reached',
      upgradeForMoreGames: 'Upgrade for More Games'
    },

    // Coach Profile
    coachProfile: {
      title: 'Goalie School Profile',
      basicInformation: 'Basic Information',
      schoolName: 'School Name',
      coachName: 'Coach Name',
      email: 'Email',
      languages: 'Languages (comma-separated)',
      languagesPlaceholder: 'e.g., English, French, German',
      description: 'Description',
      strengths: 'Strengths (comma-separated)',
      strengthsPlaceholder: 'e.g., Positioning, Rebound Control, Mental Training',
      address: 'Address',
      street: 'Street',
      streetNumber: 'Street Number',
      city: 'City',
      stateProvince: 'State/Province',
      postalCode: 'Postal Code',
      country: 'Country',
      selectCountry: 'Select a country...',
      logo: 'Logo',
      schoolLogo: 'School logo',
      socialLinks: 'Social Links',
      website: 'Website',
      instagram: 'Instagram',
      facebook: 'Facebook',
      twitter: 'Twitter',
      linkedin: 'LinkedIn',
      services: 'Services',
      addService: 'Add Service',
      serviceName: 'Service name',
      price: 'Price',
      saveProfile: 'Save Profile',
      profileSavedSuccessfully: 'Profile saved successfully!',
      failedToLoadProfile: 'Failed to load profile',
      failedToSaveProfile: 'Failed to save profile'
    },

    // Feedback Details
    feedbackDetails: {
      backToFeedback: 'Back to Feedback',
      title: 'Feedback Details',
      edit: 'Edit',
      export: 'Export',
      preparing: 'Preparing...',
      from: 'From',
      date: 'Date',
      gameDetails: 'Game Details',
      opponent: 'Opponent',
      team: 'Team',
      feedback: 'Feedback',
      suggestedDrills: 'Suggested Drills',
      addDrill: 'Add Drill',
      drillTitle: 'Drill Title',
      drillDescription: 'Drill Description',
      videoUrlOptional: 'Video URL (optional)',
      hideDrawingCanvas: 'Hide Drawing Canvas',
      drawExercise: 'Draw Exercise',
      drawMovementPattern: 'Draw the movement pattern for this drill on the rink diagram',
      cancel: 'Cancel',
      saveChanges: 'Save Changes',
      watchVideo: 'Watch Video',
      exerciseDrawing: 'Exercise Drawing',
      gameStatistics: 'Game Statistics',
      shotsFaced: 'Shots Faced',
      saves: 'Saves',
      goalsAgainst: 'Goals Against',
      savePercentage: 'Save %',
      goalDistribution: 'Goal Distribution',
      goalLocations: 'Goal Locations',
      shotDistribution: 'Shot Distribution',
      shotLocations: 'Shot Locations',
      shotTypesDistribution: 'Shot Types Distribution',
      gameSituationsDistribution: 'Game Situations Distribution',
      feedbackNotFound: 'Feedback not found',
      loadingFeedbackDetails: 'Loading feedback details...',
      failedToLoadFeedbackDetails: 'Failed to load feedback details',
      failedToUpdateFeedback: 'Failed to update feedback'
    },

    // Goalie Coaches
    goalieCoaches: {
      title: 'Find a Goalie Coach',
      subtitle: 'Connect with experienced goalie coaches and take your game to the next level',
      loading: 'Loading goalie coaches...',
      failedToLoad: 'Failed to load goalie coaches',
      search: 'Search',
      searchPlaceholder: 'Search by name or keywords...',
      language: 'Language',
      allLanguages: 'All languages',
      country: 'Country',
      selectCountry: 'Select a country...',
      city: 'City',
      searchByCity: 'Search by city...',
      languages: 'Languages',
      strengths: 'Strengths',
      services: 'Services',
      contactForPrice: 'Contact for price',
      contact: 'Contact',
      noCoachesFound: 'No goalie coaches found matching your criteria.',
      locationNotSpecified: 'Location not specified'
    },

    // PDF Exports
    pdf: {
      // Analytics PDF
      allGamesAnalytics: 'All Games Analytics',
      singleGameAnalytics: 'Single Game Analytics',
      selectedGamesAnalytics: 'Selected Games Analytics',
      dateRangeAnalytics: 'Date Range Analytics',
      goalieAnalytics: 'Goalie Analytics',
      analysisOfAll: 'Analysis of all',
      games: 'games',
      analysisOfGame: 'Analysis of game against',
      on: 'on',
      noGamesSelected: 'No games selected',
      analysisOf: 'Analysis of',
      selectedGames: 'selected games',
      analysisFrom: 'Analysis from',
      to: 'to',
      gamesInDateRange: 'games in date range',
      performanceStatistics: 'Performance Statistics',
      goalLocations: 'Goal Locations',
      goalDistribution: 'Goal Distribution',
      shotLocations: 'Shot Locations',
      shotDistribution: 'Shot Distribution',
      shotTypesDistribution: 'Shot Types Distribution',
      gameSituationsDistribution: 'Game Situations Distribution',
      gamesSummary: 'Games Summary',
      gamesSummaryContinued: 'Games Summary (Continued)',
      shotType: 'Shot Type',
      count: 'Count',
      percentage: 'Percentage',
      situation: 'Situation',
      date: 'Date',
      opponent: 'Opponent',
      shots: 'Shots',
      saves: 'Saves',
      savePercent: 'Save %',
      goalPointsExplanation: 'Each point represents a goal\'s location',
      goalZoneExplanation: 'Percentage of goals scored in each zone',
      shotPointsExplanation: 'Each point represents where a shot was taken from',
      shotZoneExplanation: 'Percentage of shots taken from each zone',
      
      // Game PDF
      gameAnalysis: 'Game Analysis',
      competitionType: 'Competition Type',
      shotsFaced: 'Shots Faced',
      savePercentage: 'Save Percentage',
      goalsAgainst: 'Goals Against',
      goalsTimeline: 'Goals Timeline',
      goal: 'Goal'
    },
    
    // Share Dialog
    shareDialog: {
      title: 'Share',
      description: 'Share this analysis with other users. They will need to be logged in to view it.',
      generateShareLink: 'Generate share link',
      done: 'Done',
      failedToCreateShare: 'Failed to create share link',
      failedToCopyLink: 'Failed to copy link'
    },

    // Upgrade Modal
    upgradeModal: {
      title: 'Upgrade to Pro',
      subtitle: 'Unlock unlimited games and AI analyses',
      whatYouGet: 'What you get:',
      unlimitedGames: 'Unlimited game tracking',
      unlimitedAnalyses: 'Unlimited AI analyses',
      prioritySupport: 'Priority support',
      guaranteeTitle: '30-Day Satisfaction Guarantee',
      guaranteeText: 'Try Pro risk-free for 30 days. If you\'re not 100% satisfied, get a full refund — no questions asked.',
      confirmCta: 'Confirm & Continue to Payment',
      cancelAnywhere: 'Cancel anytime — effective at the end of your billing period',
      annualNotice: 'Annual billing — cancel anytime'
    }
  },

  fr: {
    // Navigation
    nav: {
      games: 'Matchs',
      newGame: 'Nouveau Match',
      analytics: 'Analytiques',
      aiTrainer: 'Entraîneur IA',
      feedback: 'Commentaires',
      profile: 'Profil',
      signOut: 'Se déconnecter',
      upgradeToPro: 'Passer à Pro',
      coach: 'Entraîneur',
      featureRequests: 'Demandes de Fonctionnalités'
    },

    // Common
    common: {
      save: 'Enregistrer',
      saving: 'Enregistrement...',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      loading: 'Chargement...',
      deleting: 'Suppression...',
      error: 'Erreur',
      success: 'Succès',
      yes: 'Oui',
      no: 'Non',
      close: 'Fermer',
      back: 'Retour',
      next: 'Suivant',
      finish: 'Terminer',
      upload: 'Télécharger',
      analyze: 'Analyser',
      analyzing: 'Analyse en cours...',
      processing: 'Traitement en cours...',
      view: 'Voir',
      download: 'Télécharger',
      share: 'Partager',
      name: 'Nom',
      loadMoreSessions: 'Charger Plus de Sessions',
      futureDatesMessage: 'Vous pouvez sélectionner des dates futures pour préparer des feuilles de match à l\'avance',
      loadingYourGames: 'Chargement de vos matchs...',
      actions: 'Actions',
      notRecorded: 'Non enregistré'
    },

    // Notifications and Messages
    notifications: {
      videoAnalysisComplete: 'Analyse vidéo terminée - {count} buts générés avec analyse du gardien',
      videoNotAvailable: 'Vidéo non disponible',
      videoPlayerNotReady: 'Lecteur vidéo non prêt, veuillez réessayer',
      failedToLoadProfile: 'Échec du chargement du profil',
      failedToUpdateProfile: 'Échec de la mise à jour du profil',
      securityVerificationFailed: 'Échec de la vérification de sécurité. Veuillez réessayer.',
      failedToCreateAccount: 'Échec de la création du compte. Veuillez réessayer.',
      unexpectedError: 'Une erreur inattendue s\'est produite. Veuillez réessayer.',
      fileSizeTooLarge: 'Taille de fichier trop importante. Taille maximale : {size}MB',
      unsupportedFileType: 'Type de fichier non supporté. Veuillez télécharger des fichiers MP4, MOV, AVI, WebM ou MKV',
      invalidVideoUrl: 'Veuillez fournir une URL directe de fichier vidéo (doit se terminer par .mp4, .mov, .avi, .webm ou .mkv)',
      platformUrlNotSupported: 'Les URLs de plateformes vidéo ne sont pas supportées. Veuillez utiliser une URL directe de fichier vidéo',
      parseVideoAnalysisFailed: 'Échec de l\'analyse des résultats vidéo. Veuillez réessayer.',
      analysisUnavailable: 'Impossible de générer l\'analyse pour le moment. Veuillez réessayer plus tard.',
      dropImageHere: 'Déposez l\'image ici',
      dragDropImage: 'Glissez-déposez une image ici, ou cliquez pour sélectionner',
      maxFileSize5MB: 'Taille maximale de fichier : 5MB',
      maxFileSize50MB: 'Taille maximale de fichier : 50MB',
      liveCount: 'Compteur en direct'
    },

    // Game Details
    game: {
      title: 'Détails du Match',
      opponent: 'Adversaire',
      date: 'Date',
      competitionType: 'Type de Compétition',
      shotsFaced: 'Tirs Affrontés',
      saves: 'Arrêts',
      goalsAllowed: 'Buts Accordés',
      savePercentage: '% d\'Arrêts',
      outcome: 'Résultat',
      addGoal: 'Ajouter un But',
      editGame: 'Modifier le Match',
      deleteGame: 'Supprimer le Match',
      noGoals: 'Aucun but enregistré pour ce match.',
      addFirstGoal: 'Ajouter Votre Premier But',
      
      // Competition types
      competitionTypes: {
        'regular-season': 'Saison Régulière',
        'playoff': 'Séries Éliminatoires',
        'tournament': 'Tournoi',
        'exhibition': 'Exhibition',
        'practice': 'Entraînement'
      },

      // Outcomes
      outcomes: {
        'win': 'VICTOIRE',
        'loss': 'DÉFAITE',
        'win-ot': 'VICTOIRE PROL.',
        'loss-ot': 'DÉFAITE PROL.',
        'win-so': 'VICTOIRE TIR',
        'loss-so': 'DÉFAITE TIR'
      },
      
      // Validation and errors
      validation: {
        pleaseEnterValidDate: 'Veuillez entrer une date valide',
        pleaseProvideVideoUrl: 'Veuillez fournir une URL vidéo ou télécharger un fichier vidéo',
        failedToFetchVideo: 'Échec de récupération de la vidéo',
        failedToStartUpgrade: 'Échec du démarrage du processus de mise à niveau',
        gameNotFound: 'Match non trouvé'
      },
      
      // Game details labels
      labels: {
        saveChanges: 'Enregistrer les Modifications',
        period1: 'Période 1:',
        period2: 'Période 2:',
        period3: 'Période 3:',
        overtime: 'Prolongation:',
        uploadVideoFile: 'Télécharger un Fichier Vidéo',
        submittingToAI: 'Soumission à l\'IA...',
        shotLocation: 'Emplacement du Tir',
        goalLocation: 'Emplacement du But',
        aiGoalieAnalysis: 'Analyse du Gardien IA',
        goalieTechnique: 'Technique du Gardien',
        videoUrlRequirement: 'Doit être une URL directe de fichier vidéo (.mp4, .mov, .avi, .webm, .mkv)',
        aiPrefilledMessage: 'L\'IA a pré-rempli les emplacements des tirs et des buts. Vous pouvez les ajuster en cliquant sur les diagrammes ci-dessous.',
        situationDescription: 'Description de la Situation',
        videoUrl: 'URL de la Vidéo',
        url: 'URL',
        upload: 'Fichier'
      }
    },

    // Live Shots Counter
    liveShotsCounter: {
      title: 'Compteur en direct',
      period1: 'Période 1',
      period2: 'Période 2', 
      period3: 'Période 3',
      overtime: 'Prolongation',
      totalShots: 'Total des Tirs',
      cancel: 'Annuler',
      saveShots: 'Enregistrer les Tirs'
    },

    // Feedback List
    feedbackList: {
      noFeedbackYet: 'Aucun commentaire pour le moment',
      unknownRecipient: 'Destinataire Inconnu',
      suggestedDrills: 'Exercices Suggérés',
      watchVideo: 'Regarder la Vidéo',
      exerciseDrawing: 'Dessin d\'Exercice',
      viewDetails: 'Voir les Détails',
      deleteFeedback: 'Supprimer le Commentaire',
      deleteFeedbackTitle: 'Supprimer le Commentaire',
      deleteFeedbackConfirmation: 'Êtes-vous sûr de vouloir supprimer ce commentaire ? Cette action ne peut pas être annulée.',
      cancel: 'Annuler'
    },

    // Saves List
    savesList: {
      title: 'Arrêts et Bonnes Choses',
      addSave: 'Ajouter un Arrêt',
      description: 'Ajoutez ce que vous avez bien fait pendant le match - mettez en valeur vos meilleurs arrêts et moments mémorables.',
      titleLabel: 'Titre',
      titlePlaceholder: 'ex: Excellent Arrêt du Gant',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Décrivez l\'arrêt ou la situation...',
      cancel: 'Annuler',
      adding: 'Ajout en cours...',
      addSaveButton: 'Ajouter l\'Arrêt',
      noSavesYet: 'Aucun arrêt enregistré pour le moment',
      deleteSave: 'Supprimer l\'Arrêt',
      deleteSaveTitle: 'Supprimer l\'Arrêt',
      deleteSaveConfirmation: 'Êtes-vous sûr de vouloir supprimer cet arrêt ? Cette action ne peut pas être annulée.'
    },

    // Shot Analysis Card
    shotAnalysisCard: {
      technique: 'Technique :',
      viewVideoClip: 'Voir le Clip Vidéo',
      whatWentWell: 'Ce qui a Bien Fonctionné',
      areasForImprovement: 'Domaines à Améliorer',
      keyMoment: 'Moment Clé',
      coachingTip: 'Conseil d\'Entraînement',
      shotVisualization: 'Visualisation du Tir',
      shotOriginPosition: 'Position d\'Origine du Tir',
      shotDestinationOnGoal: 'Destination du Tir sur le But'
    },

    // Feedback Form
    feedbackForm: {
      feedbackPlaceholder: 'Partagez vos pensées, suggestions ou observations...',
      suggestedDrills: 'Exercices Suggérés',
      addDrill: 'Ajouter un Exercice',
      drillTitle: 'Titre de l\'Exercice',
      drillTitlePlaceholder: 'ex: Exercice de Mouvement Latéral',
      description: 'Description',
      descriptionPlaceholder: 'Décrivez l\'exercice et comment l\'exécuter...',
      videoUrlOptional: 'URL Vidéo (optionnel)',
      videoUrlPlaceholder: 'https://...'
    },

    // Goal Form
    goalForm: {
      title: 'Ajouter un But',
      editTitle: 'Modifier le But',
      goalTitle: 'Titre du But',
      shotType: 'Type de Tir',
      gameSituation: 'Situation de Jeu',
      situationDescription: 'Description de la Situation',
      clipTiming: 'Temps du Clip',
      startTime: 'Temps de Début',
      endTime: 'Temps de Fin',
      goalPosition: 'Position du But',
      shotPosition: 'Position du Tir',
      videoAnalysis: 'Analyse Vidéo IA',
      analyzeVideo: 'Analyser la Vidéo',
      uploadVideo: 'Télécharger la Vidéo',
      videoUrl: 'URL de la Vidéo',
      analyzing: 'Analyse de la vidéo...',
      uploading: 'Téléchargement de la vidéo...',
      
      // Shot types
      shotTypes: {
        'Wrist Shot': 'Tir du Poignet',
        'Slap Shot': 'Lancer Frappé',
        'Snap Shot': 'Tir Rapide',
        'Backhand': 'Revers',
        'Deflection / Tip-in': 'Déviation',
        'One-Timer': 'Tir Direct',
        'Wraparound': 'Contournement',
        'Rebound Shot': 'Retour de Lancer'
      },

      // Game situations
      gameSituations: {
        'Break away': 'Échappée',
        'Penalty Shot': 'Tir de Pénalité',
        'Screened Shot': 'Tir Masqué',
        'Odd-Man Rush': 'Avantage Numérique',
        'Cross-Ice Pass': 'Passe Transversale',
        'Point Shot': 'Tir de la Pointe',
        'Net-Front Battle': 'Bataille Devant le Filet',
        'Shorthanded Goal': 'But en Infériorité',
        'Power Play Goal': 'But en Avantage',
        'Turnover Leading to Goal': 'Revirement Menant au But',
        'Off the Rush': 'En Montée',
        'Puck Battle / Scramble': 'Bataille pour la Rondelle'
      },

      validation: {
        titleRequired: 'Le titre du but est requis',
        shotTypeRequired: 'Le type de tir est requis',
        gameSituationRequired: 'La situation de jeu est requise',
        positionRequired: 'Veuillez cliquer sur le diagramme du but pour définir la position',
        shotPositionRequired: 'Veuillez cliquer sur le diagramme de tir pour définir la position du tir'
      },

      aiAnalysis: {
        preFilled: 'L\'IA a pré-rempli l\'emplacement du tir. Vous pouvez l\'ajuster en cliquant sur le diagramme.',
        technique: 'Technique du Gardien',
        whatWentWell: 'Ce qui a Bien Fonctionné',
        areasForImprovement: 'Domaines d\'Amélioration',
        keyMoment: 'Moment Clé',
        coachingTip: 'Conseil d\'Entraînement'
      }
    },

    // Video Player
    videoPlayer: {
      showPlayer: 'Afficher le Lecteur',
      hidePlayer: 'Masquer le Lecteur',
      viewClip: 'Voir le Clip'
    },

    // AI Trainer
    aiTrainer: {
      title: 'Entraîneur IA',
      subtitle: 'Vos sessions d\'entraînement de gardien personnalisées alimentées par l\'IA',
      analyzeWithAI: 'Analyser avec l\'Entraîneur IA',
      analyzing: 'Analyse en cours...',
      sessions: 'Sessions d\'Entraînement IA',
      noSessions: 'Aucune session d\'entraînement IA pour le moment.',
      createFirst: 'Créez votre première analyse pour obtenir des conseils d\'entraînement personnalisés.',
      limitReached: 'Limite d\'Analyse IA Atteinte',
      limitMessage: 'Vous avez utilisé {used} des {limit} analyses gratuites. Passez à la version supérieure pour continuer.',
      upgradeRequired: 'Vous avez atteint votre limite d\'analyse IA gratuite. Veuillez mettre à niveau pour continuer.',
      
      // Analysis sections
      analysis: {
        title: 'Analyse de Performance',
        drills: 'Exercices Recommandés',
        tips: 'Conseils Rapides',
        motivation: 'Note Motivationnelle'
      }
    },

    // Dashboard
    dashboard: {
      title: 'Tableau de Bord des Matchs',
      recentGames: 'Matchs Récents',
      quickStats: 'Statistiques Rapides',
      totalGames: 'Total des Matchs',
      totalSaves: 'Total des Arrêts',
      avgSavePercentage: '% d\'Arrêts Moyen',
      noGames: 'Aucun match enregistré pour le moment.',
      createFirst: 'Créez votre premier match pour commencer à suivre vos performances.',
      trackPerformance: 'Suivez vos performances et analysez vos progrès',
      newFeatures: 'Nouvelles Fonctionnalités!',
      recordNewGame: 'Enregistrer un Nouveau Match',
      averageSavePercentage: '% d\'Arrêts Moyen',
      date: 'Date',
      opponent: 'Adversaire',
      competition: 'Compétition',
      savePercentage: '% d\'Arrêts',
      goals: 'Buts',
      actions: 'Actions'
    },

    // Analytics
    analytics: {
      title: 'Analytiques',
      subtitle: 'Analysez vos modèles de performance et tendances',
      overview: 'Aperçu',
      trends: 'Tendances',
      breakdown: 'Répartition',
      allShotTypesDistribution: 'Répartition de Tous les Types de Tirs',
      allGameSituationsDistribution: 'Répartition de Toutes les Situations de Jeu',
      selectGames: 'Sélectionner les Matchs',
      selectAll: 'Tout Sélectionner',
      clearSelection: 'Effacer la Sélection',
      selectedGames: 'Matchs Sélectionnés',
      gameOutcomesBreakdown: 'Répartition des Résultats de Match',
      top5ShotTypes: 'Top 5 des Types de Tirs',
      allShotTypes: 'Tous les Types de Tirs',
      top5GameSituations: 'Top 5 des Situations de Jeu',
      allGameSituations: 'Toutes les Situations de Jeu',
      showAll: 'Tout Afficher',
      showTop5: 'Afficher le Top 5',
      noGamesSelected: 'Aucun match sélectionné. Veuillez sélectionner au moins un match pour voir les analytiques.',
      selectGamesToAnalyze: 'Sélectionner les matchs à analyser',
      clickToToggle: 'Cliquer pour basculer la sélection',
      exportPDF: 'Exporter en PDF',
      shareAnalytics: 'Partager les Analytiques',
      upgradeToUnlock: 'Passer à la version supérieure pour débloquer cette fonctionnalité',
      premiumFeature: 'Fonctionnalité Premium',
      dateRange: 'Plage de Dates',
      from: 'De',
      to: 'À',
      all: 'Tous',
      single: 'Simple',
      multiple: 'Multiple',
      range: 'Plage',
      seeAllShotTypes: 'Voir Tous les Types de Tirs',
      seeAllGameSituations: 'Voir Toutes les Situations de Jeu',
      top5ShotTypesDistribution: 'Répartition du Top 5 des Types de Tirs',
      top5GameSituationsDistribution: 'Répartition du Top 5 des Situations de Jeu',
      analyticsResults: 'Résultats d\'Analytiques',
      detailedVisualization: 'Visualisation détaillée de vos données de performance',
      goalDistributionHeatmap: 'Carte de Chaleur de la Répartition des Buts',
      heatmapDescription: 'La carte de chaleur montre le pourcentage de buts marqués dans chaque zone.',
      shotPositionAnalysis: 'Analyse de Position de Tir',
      winPercentage: 'Pourcentage de Victoires',
      importantNote: 'Note Importante',
      accurateStatisticsNote: 'Pour des statistiques précises, vous devez ajouter des buts sur le diagramme pour chaque match. Les statistiques sont calculées en fonction des buts que vous avez enregistrés, et non seulement des résultats de match.',
      totalGoalsAgainst: 'Total des Buts Encaissés',
      acrossGames: 'Sur {count} matchs',
      averageGoalsPerGame: 'Moyenne de Buts par Match',
      basedOnSelectedData: 'Basé sur les données sélectionnées',
      savePercentage: 'Pourcentage d\'Arrêts',
      overallSavePercentage: 'Pourcentage d\'arrêts global',
      basedOnGamesWithOutcomes: 'Basé sur les matchs avec résultats',
      acrossSelectedGames: 'Sur {count} match{plural} sélectionné{plural}',
      goalLocations: 'Emplacements des Buts',
      goalPointsExplanation: 'Chaque point représente l\'emplacement d\'un but',
      upgradeForAIAnalysis: 'Mettre à niveau pour l\'analyse IA',
      shotDistributionHeatmap: 'Carte de Chaleur de Répartition des Tirs',
      shotLocations: 'Emplacements des Tirs',
      shotHeatmapDescription: 'La carte de chaleur montre le pourcentage de tirs effectués depuis chaque zone. Le rouge plus foncé indique un pourcentage plus élevé de tirs.',
      shotPointsDescription: 'Chaque point représente l\'endroit d\'où un tir a été effectué.',
      seekingTo: 'Recherche vers',
      
      // Risk Analysis
      riskAnalysis: 'Analyse des Risques',
      riskAnalysisCaption: 'Informations clés sur les performances basées sur vos données de buts et d\'arrêts',
      addMoreGoalsToSeeAnalysis: 'Ajoutez plus de buts pour voir l\'analyse',
      topStrength: 'Principale Force',
      topWeakness: 'Principale Faiblesse',
      mostRiskySituation: 'Situation la Plus Risquée',
      mostRiskyShotType: 'Type de Tir le Plus Risqué'
    },

    // AI Trainer Session
    aiTrainerSession: {
      title: 'Session d\'Entraîneur IA',
      backToAITrainer: 'Retour à l\'Entraîneur IA',
      sessionNotFound: 'Session non trouvée',
      permissionDenied: 'Vous n\'avez pas la permission de voir cette session',
      loadingSession: 'Chargement de la session d\'entraînement IA...',
      failedToLoad: 'Échec du chargement de la session d\'entraîneur IA',
      gamesAnalyzed: 'Matchs analysés',
      analyzed: 'analysés',
      aiCoachAnalysis: 'Analyse de l\'Entraîneur IA',
      recommendedDrills: 'Exercices Recommandés',
      performanceTips: 'Conseils de Performance',
      motivationalNote: 'Note Motivationnelle',
      goalDistribution: 'Répartition des Buts',
      goalLocations: 'Emplacements des Buts',
      shotDistribution: 'Répartition des Tirs',
      shotLocations: 'Emplacements des Tirs',
      sessionStats: 'Statistiques de Session',
      totalGoals: 'Total des Buts',
      drillsRecommended: 'Exercices Recommandés',
      tipsProvided: 'Conseils Fournis',
      watchVideo: 'Regarder la Vidéo',
      vs: 'vs'
    },

    // Admin Panel
    adminPanel: {
      title: 'Panneau d\'Administration',
      signInToManage: 'Connectez-vous pour gérer les paramètres système',
      username: 'Nom d\'utilisateur',
      password: 'Mot de passe',
      signingIn: 'Connexion en cours...',
      signIn: 'Se connecter',
      systemSettings: 'Paramètres Système',
      logout: 'Déconnexion',
      freeGamesLimit: 'Limite de Matchs Gratuits',
      freeGamesDescription: 'Nombre de matchs que les utilisateurs peuvent créer gratuitement',
      freeAIAnalysisLimit: 'Limite d\'Analyses IA Gratuites',
      freeAnalysisDescription: 'Nombre d\'analyses IA que les utilisateurs peuvent générer gratuitement',
      saving: 'Enregistrement...',
      saveSettings: 'Enregistrer les Paramètres',
      settingsUpdatedSuccessfully: 'Paramètres mis à jour avec succès!',
      stripeProductsAndPricing: 'Produits Stripe et Tarification',
      loadingProducts: 'Chargement des produits...',
      currentPrice: 'Prix Actuel',
      noActivePrices: 'Aucun prix actif',
      updatePrice: 'Mettre à jour le Prix ($)',
      updating: 'Mise à jour...',
      updatePriceButton: 'Mettre à jour le Prix',
      priceUpdatedSuccessfully: 'Prix mis à jour avec succès pour le produit',
      loginFailed: 'Échec de la connexion',
      updateFailed: 'Échec de la mise à jour',
      pleaseEnterValidPrice: 'Veuillez entrer un prix valide',
      failedToFetchProducts: 'Échec de récupération des produits Stripe',
      failedToUpdatePrice: 'Échec de la mise à jour du prix'
    },

    // Coach Dashboard
    coachDashboard: {
      title: 'Tableau de Bord d\'Entraîneur',
      subtitle: 'Gérez vos activités d\'entraînement et paramètres de profil',
      studentManagement: 'Gestion des Étudiants',
      manageStudentsAndSessions: 'Gérez vos étudiants et sessions d\'entraînement',
      trackStudentProgress: 'Suivre les progrès des étudiants',
      planPracticeSessions: 'Planifier les sessions d\'entraînement',
      monitorDevelopment: 'Surveiller le développement',
      goalieSchoolProfile: 'Profil d\'École de Gardien',
      manageCoachingProfile: 'Gérez votre profil d\'entreprise d\'entraînement',
      publicCoachingProfile: 'Profil d\'entraînement public',
      servicesAndPricing: 'Services et tarification',
      contactInformation: 'Informations de contact',
      gettingStartedAsCoach: 'Commencer en tant qu\'Entraîneur',
      setupProfileDescription: 'Configurez votre profil d\'entraîneur pour attirer de nouveaux étudiants et utilisez les outils de gestion des étudiants pour suivre leurs progrès.',
      createGoalieSchoolProfile: 'Créez votre profil d\'école de gardien pour être découvrable par les étudiants potentiels',
      addStudentsToTrack: 'Ajoutez des étudiants pour suivre leur développement et planifier des sessions d\'entraînement ciblées',
      useAnalyticsTools: 'Utilisez les outils d\'analytique pour fournir des commentaires détaillés à vos étudiants'
    },

    // Feedback
    feedback: {
      title: 'Commentaires',
      hireACoach: 'Embaucher un Entraîneur',
      loadingFeedback: 'Chargement des commentaires...',
      failedToLoadFeedback: 'Échec du chargement des commentaires',
      received: 'Reçus',
      sent: 'Envoyés',
      searchFeedback: 'Rechercher des Commentaires',
      searchPlaceholder: 'Rechercher par contenu, auteur ou adversaire...',
      dateRange: 'Plage de Dates'
    },

    // Shared View
    sharedView: {
      title: 'Analyse Partagée',
      sharedBy: 'Partagé par',
      loadingSharedAnalysis: 'Chargement de l\'analyse partagée...',
      shareNotFound: 'Partage non trouvé',
      failedToLoadSharedData: 'Échec du chargement des données partagées. Veuillez réessayer.',
      preparingPDF: 'Préparation du PDF...',
      exportPDF: 'Exporter en PDF',
      totalGoalsAgainst: 'Total des Buts Encaissés',
      acrossGames: 'Sur {count} matchs',
      averageGoalsPerGame: 'Moyenne de Buts par Match',
      basedOnAllGames: 'Basé sur tous les matchs',
      basedOnSelectedData: 'Basé sur les données sélectionnées',
      savePercentage: 'Pourcentage d\'Arrêts',
      overallSavePercentage: 'Pourcentage d\'arrêts global',
      basedOnGamesWithOutcomes: 'Basé sur les matchs avec résultats',
      acrossSelectedGames: 'Sur {count} match{plural} sélectionné{plural}',
      importantNote: 'Note Importante',
      accurateStatisticsNote: 'Pour des statistiques précises, vous devez ajouter des buts sur le diagramme pour chaque match. Les statistiques sont calculées en fonction des buts que vous avez enregistrés, et non seulement des résultats de match.',
      feedback: 'Commentaires',
      selectGameForFeedback: 'Sélectionner un Match pour Commentaires',
      goalDistributionHeatmap: 'Carte de Chaleur de la Répartition des Buts',
      shotPositionAnalysis: 'Analyse de Position de Tir',
      savesAndGoodThings: 'Arrêts et Bonnes Choses',
      highlightsAndMoments: 'Moments forts et mémorables de ce match',
      goalsTimeline: 'Chronologie des Buts',
      noGoalsRecorded: 'Aucun but enregistré pour ce match',
      shotTypesDistribution: 'Répartition des Types de Tirs',
      allFeedback: 'Tous les Commentaires',
      goalLocations: 'Emplacements des Buts',
      shotLocations: 'Emplacements des Tirs',
      heatmapExplanation: 'La carte de chaleur montre le pourcentage de buts marqués dans chaque zone. Le rouge plus foncé indique un pourcentage plus élevé de buts.',
      goalPointsExplanation: 'Chaque point représente l\'emplacement d\'un but.',
      shotHeatmapExplanation: 'La carte de chaleur montre le pourcentage de tirs effectués depuis chaque zone.',
      shotPointsExplanation: 'Chaque point représente l\'endroit d\'où un tir a été effectué.'
    },

    // Game Details  
    gameDetails: {
      upgradeForAIAnalysis: 'Mettre à Niveau pour l\'Analyse IA',
      analyzeGamePerformance: 'Analysez vos performances de match et suivez les patterns de buts',
      exportPDF: 'Exporter en PDF',
      share: 'Partager',
      shotsFaced: 'Tirs Affrontés',
      shots: 'Tirs',
      totalShotsFaced: 'Total des tirs affrontés',
      saves: 'Arrêts',
      totalSavesMade: 'Total des arrêts effectués',
      goalsAgainst: 'Buts Contre',
      goalsConceded: 'Buts encaissés',
      aiVideoAnalysis: 'Analyse Vidéo IA',
      betaDisclaimer: 'Les buts générés par l\'IA peuvent avoir des positions de tir, types et détails inexacts. Veuillez les réviser et les modifier dans la liste des buts ci-dessous.',
      analyzeVideoForGoals: 'Analyser la Vidéo pour les Buts',
      goalsAnalysis: 'Analyse des Buts',
      goalLocation: 'Position du But',
      shotLocation: 'Position du Tir',
      goals: 'Buts',
      whatWentWell: 'Ce qui a Bien Fonctionné',
      areasForImprovement: 'Domaines à Améliorer',
      keyMoment: 'Moment Clé',
      coachingTip: 'Conseil d\'Entraînement',
      viewClip: 'Voir le Clip',
      deleteGoal: 'Supprimer le But',
      deleteGame: 'Supprimer le Match',
      selectOutcome: 'Sélectionner le résultat',
      goalTitlePlaceholder: 'ex: But en Échappée',
      situationDescriptionPlaceholder: 'Décrivez la situation menant au but...',
      videoUrlPlaceholder: 'Entrez l\'URL directe de la vidéo (ex: https://exemple.com/video.mp4)',
      cancelEditing: 'Annuler la modification',
      editGame: 'Modifier le match',
      editGoal: 'Modifier le but',
      selectShotType: 'Sélectionner le type de tir',
      selectGameSituation: 'Sélectionner la situation de jeu',
      savesCalculatedAutomatically: 'Les arrêts seront calculés automatiquement en fonction des tirs affrontés et des buts contre',
      gameOutcome: 'Résultat du Match (optionnel)'
    },

    // Student Management
    studentManagement: {
      title: 'Gestion des Étudiants',
      addStudent: 'Ajouter un Étudiant',
      editStudent: 'Modifier l\'Étudiant',
      addNewStudent: 'Ajouter un Nouvel Étudiant',
      updateStudent: 'Mettre à jour l\'Étudiant',
      deleteStudent: 'Supprimer l\'Étudiant',
      loadingStudents: 'Chargement des étudiants...',
      noStudentsYet: 'Aucun étudiant pour le moment',
      noStudentsDescription: 'Ajoutez votre premier étudiant pour commencer à suivre ses progrès.',
      addFirstStudent: 'Ajouter Votre Premier Étudiant',
      practiceSessions: 'Sessions d\'Entraînement',
      addPractice: 'Ajouter une Pratique',
      updatePractice: 'Mettre à jour la Pratique',
      noPracticeSessionsYet: 'Aucune session d\'entraînement enregistrée pour le moment',
      noPracticeSessionsDescription: 'Commencez à ajouter des sessions d\'entraînement pour suivre les progrès.',
      firstName: 'Prénom',
      lastName: 'Nom de Famille',
      age: 'Âge',
      level: 'Niveau',
      team: 'Équipe',
      strengths: 'Forces',
      areasToImprove: 'Domaines à Améliorer',
      notes: 'Notes',
      date: 'Date',
      duration: 'Durée (minutes)',
      focusAreas: 'Domaines de Focus',
      progress: 'Progrès',
      drills: 'Exercices',
      addDrill: 'Ajouter un Exercice',
      drillTitle: 'Titre de l\'Exercice',
      drillDescription: 'Description',
      videoUrl: 'URL Vidéo (optionnel)',
      failedToLoadStudents: 'Échec du chargement des étudiants et sessions d\'entraînement',
      failedToSaveStudent: 'Échec de l\'enregistrement de l\'étudiant',
      failedToSavePractice: 'Échec de l\'enregistrement de la session d\'entraînement',
      failedToDeleteStudent: 'Échec de la suppression de l\'étudiant',
      failedToDeletePractice: 'Échec de la suppression de la session d\'entraînement',
      deleteStudentConfirmation: 'Êtes-vous sûr de vouloir supprimer cet étudiant? Cette action ne peut pas être annulée.',
      deletePracticeConfirmation: 'Êtes-vous sûr de vouloir supprimer cette session d\'entraînement? Cette action ne peut pas être annulée.',
      cancel: 'Annuler',
      save: 'Enregistrer',
      update: 'Mettre à jour',
      delete: 'Supprimer',
      practiceMinutes: 'minutes'
    },

    // Upvotes/Feature Requests
    upvotes: {
      title: 'Demandes de Fonctionnalité',
      subtitle: 'Votez pour les fonctionnalités que vous aimeriez voir dans GoalR ou suggérez-en de nouvelles',
      newRequest: 'Nouvelle Demande',
      newFeatureRequest: 'Nouvelle Demande de Fonctionnalité',
      loadingFeatureRequests: 'Chargement des demandes de fonctionnalité...',
      failedToLoadRequests: 'Échec du chargement des demandes de fonctionnalité',
      failedToAddRequest: 'Échec de l\'ajout de la demande de fonctionnalité',
      failedToUpdateVote: 'Échec de la mise à jour du vote',
      failedToDeleteRequest: 'Échec de la suppression de la demande de fonctionnalité',
      requestTitle: 'Titre',
      titlePlaceholder: 'Quelle fonctionnalité aimeriez-vous voir?',
      description: 'Description',
      descriptionPlaceholder: 'Décrivez la fonctionnalité et comment elle aiderait...',
      cancel: 'Annuler',
      submitRequest: 'Soumettre la Demande',
      requestedBy: 'Demandé par',
      deleteFeatureRequest: 'Supprimer la Demande de Fonctionnalité',
      deleteConfirmation: 'Êtes-vous sûr de vouloir supprimer cette demande de fonctionnalité? Cette action ne peut pas être annulée.',
      deleteRequest: 'Supprimer la Demande'
    },

    // Profile
    profile: {
      title: 'Profil',
      personalInfo: 'Informations Personnelles',
      firstName: 'Prénom',
      lastName: 'Nom de Famille',
      age: 'Âge',
      level: 'Niveau',
      team: 'Équipe',
      subscription: 'Abonnement',
      usage: 'Utilisation',
      gamesCreated: 'Matchs Créés',
      analysesCreated: 'Analyses Créées',
      upgradeDescription: 'Obtenez l\'accès aux fonctionnalités avancées et aux analyses illimitées:',
      unlimitedGameAnalysis: 'Analyse de match illimitée',
      advancedAIFeatures: 'Fonctionnalités d\'entraînement IA avancées',
      prioritySupport: 'Support prioritaire',
      upgradeNow: 'Passer à la Version Supérieure'
    },

    // Authentication
    auth: {
      signIn: 'Se Connecter',
      signUp: 'S\'Inscrire',
      email: 'Courriel',
      password: 'Mot de Passe',
      confirmPassword: 'Confirmer le Mot de Passe',
      forgotPassword: 'Mot de passe oublié?',
      sending: 'Envoi en cours...',
      signingIn: 'Connexion en cours...',
      signingUp: 'Inscription en cours...',
      creatingAccount: 'Création du Compte...',
      dontHaveAccount: 'Vous n\'avez pas de compte?',
      alreadyHaveAccount: 'Vous avez déjà un compte?',
      resetPasswordSent: 'Courriel de réinitialisation envoyé! Veuillez vérifier votre boîte de réception.',
      invalidEmailPassword: 'Courriel ou mot de passe invalide',
      enterEmailAddress: 'Veuillez entrer votre adresse courriel',
      passwordResetFailed: 'Échec de l\'envoi du courriel de réinitialisation. Veuillez vérifier votre adresse courriel.',
      createAccount: 'Créer un Compte',
      firstName: 'Prénom',
      lastName: 'Nom de Famille',
      age: 'Âge',
      level: 'Niveau',
      team: 'Équipe',
      selectLevel: 'Sélectionnez un niveau',
      recreational: 'Récréatif',
      junior: 'Junior',
      highSchool: 'Secondaire',
      college: 'Collégial',
      semiProfessional: 'Semi-Professionnel',
      professional: 'Professionnel'
    },

    // Errors and Messages
    messages: {
      gameNotFound: 'Match non trouvé',
      loadingError: 'Échec du chargement des données',
      saveError: 'Échec de l\'enregistrement',
      deleteError: 'Échec de la suppression',
      uploadError: 'Échec du téléchargement',
      analysisError: 'Échec de l\'analyse vidéo',
      networkError: 'Erreur réseau. Veuillez vérifier votre connexion.',
      sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
      unauthorizedAccess: 'Vous n\'avez pas l\'autorisation d\'accéder à cette ressource.',
      videoTooLarge: 'Le fichier vidéo est trop volumineux. Veuillez choisir un fichier plus petit.',
      unsupportedFormat: 'Format de fichier non pris en charge. Veuillez utiliser MP4, MOV ou AVI.',
      
      // Success messages
      gameSaved: 'Match enregistré avec succès',
      goalSaved: 'But enregistré avec succès',
      goalDeleted: 'But supprimé avec succès',
      gameDeleted: 'Match supprimé avec succès',
      videoUploaded: 'Vidéo téléchargée avec succès',
      analysisComplete: 'Analyse terminée avec succès',
      profileNotFound: 'Profil non trouvé',
      aiAnalyzingVideo: 'L\'IA analyse votre vidéo pour identifier les tirs et les arrêts. Cela peut prendre quelques instants...',
      confirmDeleteGoal: 'Êtes-vous sûr de vouloir supprimer ce but ? Cette action ne peut pas être annulée.',
      confirmDeleteGame: 'Êtes-vous sûr de vouloir supprimer ce match et tous les buts associés ? Cette action ne peut pas être annulée.'
    },

    // New Game
    newGame: {
      title: 'Enregistrer un Nouveau Match',
      subtitle: 'Suivez vos performances et améliorez vos compétences',
      gameDate: 'Date du Match',
      opponent: 'Adversaire',
      opponentPlaceholder: 'Entrez le nom de l\'équipe adverse',
      competitionType: 'Type de Compétition',
      shotsFaced: 'Tirs Affrontés (Optionnel)',
      saves: 'Arrêts (Optionnel)', 
      gameOutcome: 'Résultat du Match (Optionnel)',
      selectOutcome: 'Sélectionner le résultat',
      clearSelection: 'Effacer la sélection',
      creatingGame: 'Création du Match...',
      createGame: 'Créer le Match',
      cancel: 'Annuler',
      gameLimitReached: 'Limite de Matchs Atteinte',
      upgradeForMoreGames: 'Passez à la Version Supérieure pour Plus de Matchs'
    },

    // Coach Profile
    coachProfile: {
      title: 'Profil École de Gardiens',
      basicInformation: 'Informations de Base',
      schoolName: 'Nom de l\'École',
      coachName: 'Nom de l\'Entraîneur',
      email: 'Courriel',
      languages: 'Langues (séparées par des virgules)',
      languagesPlaceholder: 'ex: Anglais, Français, Allemand',
      description: 'Description',
      strengths: 'Forces (séparées par des virgules)',
      strengthsPlaceholder: 'ex: Positionnement, Contrôle des Retours, Entraînement Mental',
      address: 'Adresse',
      street: 'Rue',
      streetNumber: 'Numéro de Rue',
      city: 'Ville',
      stateProvince: 'Province/État',
      postalCode: 'Code Postal',
      country: 'Pays',
      selectCountry: 'Sélectionner un pays...',
      logo: 'Logo',
      schoolLogo: 'Logo de l\'école',
      socialLinks: 'Liens Sociaux',
      website: 'Site Web',
      instagram: 'Instagram',
      facebook: 'Facebook',
      twitter: 'Twitter',
      linkedin: 'LinkedIn',
      services: 'Services',
      addService: 'Ajouter un Service',
      serviceName: 'Nom du service',
      price: 'Prix',
      saveProfile: 'Enregistrer le Profil',
      profileSavedSuccessfully: 'Profil enregistré avec succès!',
      failedToLoadProfile: 'Échec du chargement du profil',
      failedToSaveProfile: 'Échec de l\'enregistrement du profil'
    },

    // Feedback Details
    feedbackDetails: {
      backToFeedback: 'Retour aux Commentaires',
      title: 'Détails des Commentaires',
      edit: 'Modifier',
      export: 'Exporter',
      preparing: 'Préparation...',
      from: 'De',
      date: 'Date',
      gameDetails: 'Détails du Match',
      opponent: 'Adversaire',
      team: 'Équipe',
      feedback: 'Commentaires',
      suggestedDrills: 'Exercices Suggérés',
      addDrill: 'Ajouter un Exercice',
      drillTitle: 'Titre de l\'Exercice',
      drillDescription: 'Description de l\'Exercice',
      videoUrlOptional: 'URL de la Vidéo (optionnel)',
      hideDrawingCanvas: 'Masquer le Canevas de Dessin',
      drawExercise: 'Dessiner l\'Exercice',
      drawMovementPattern: 'Dessinez le pattern de mouvement pour cet exercice sur le diagramme de la patinoire',
      cancel: 'Annuler',
      saveChanges: 'Enregistrer les Modifications',
      watchVideo: 'Regarder la Vidéo',
      exerciseDrawing: 'Dessin de l\'Exercice',
      gameStatistics: 'Statistiques du Match',
      shotsFaced: 'Tirs Affrontés',
      saves: 'Arrêts',
      goalsAgainst: 'Buts Contre',
      savePercentage: '% d\'Arrêts',
      goalDistribution: 'Distribution des Buts',
      goalLocations: 'Emplacements des Buts',
      shotDistribution: 'Distribution des Tirs',
      shotLocations: 'Emplacements des Tirs',
      shotTypesDistribution: 'Distribution des Types de Tirs',
      gameSituationsDistribution: 'Distribution des Situations de Jeu',
      feedbackNotFound: 'Commentaires non trouvés',
      loadingFeedbackDetails: 'Chargement des détails des commentaires...',
      failedToLoadFeedbackDetails: 'Échec du chargement des détails des commentaires',
      failedToUpdateFeedback: 'Échec de la mise à jour des commentaires'
    },

    // Goalie Coaches
    goalieCoaches: {
      title: 'Trouver un Entraîneur de Gardiens',
      subtitle: 'Connectez-vous avec des entraîneurs de gardiens expérimentés et élevez votre jeu au niveau supérieur',
      loading: 'Chargement des entraîneurs de gardiens...',
      failedToLoad: 'Échec du chargement des entraîneurs de gardiens',
      search: 'Rechercher',
      searchPlaceholder: 'Rechercher par nom ou mots-clés...',
      language: 'Langue',
      allLanguages: 'Toutes les langues',
      country: 'Pays',
      selectCountry: 'Sélectionner un pays...',
      city: 'Ville',
      searchByCity: 'Rechercher par ville...',
      languages: 'Langues',
      strengths: 'Forces',
      services: 'Services',
      contactForPrice: 'Contactez pour le prix',
      contact: 'Contact',
      noCoachesFound: 'Aucun entraîneur de gardiens trouvé correspondant à vos critères.',
      locationNotSpecified: 'Emplacement non spécifié'
    },

    // PDF Exports
    pdf: {
      // Analytics PDF
      allGamesAnalytics: 'Analytiques de Tous les Matchs',
      singleGameAnalytics: 'Analytiques d\'un Match',
      selectedGamesAnalytics: 'Analytiques des Matchs Sélectionnés',
      dateRangeAnalytics: 'Analytiques de Plage de Dates',
      goalieAnalytics: 'Analytiques du Gardien',
      analysisOfAll: 'Analyse de tous',
      games: 'matchs',
      analysisOfGame: 'Analyse du match contre',
      on: 'le',
      noGamesSelected: 'Aucun match sélectionné',
      analysisOf: 'Analyse de',
      selectedGames: 'matchs sélectionnés',
      analysisFrom: 'Analyse du',
      to: 'au',
      gamesInDateRange: 'matchs dans la plage de dates',
      performanceStatistics: 'Statistiques de Performance',
      goalLocations: 'Emplacements des Buts',
      goalDistribution: 'Répartition des Buts',
      shotLocations: 'Emplacements des Tirs',
      shotDistribution: 'Répartition des Tirs',
      shotTypesDistribution: 'Répartition des Types de Tirs',
      gameSituationsDistribution: 'Répartition des Situations de Jeu',
      gamesSummary: 'Résumé des Matchs',
      gamesSummaryContinued: 'Résumé des Matchs (Suite)',
      shotType: 'Type de Tir',
      count: 'Nombre',
      percentage: 'Pourcentage',
      situation: 'Situation',
      date: 'Date',
      opponent: 'Adversaire',
      shots: 'Tirs',
      saves: 'Arrêts',
      savePercent: '% d\'Arrêts',
      goalPointsExplanation: 'Chaque point représente l\'emplacement d\'un but',
      goalZoneExplanation: 'Pourcentage de buts marqués dans chaque zone',
      shotPointsExplanation: 'Chaque point représente l\'endroit d\'où un tir a été effectué',
      shotZoneExplanation: 'Pourcentage de tirs effectués depuis chaque zone',
      
      // Game PDF
      gameAnalysis: 'Analyse du Match',
      competitionType: 'Type de Compétition',
      shotsFaced: 'Tirs Affrontés',
      savePercentage: 'Pourcentage d\'Arrêts',
      goalsAgainst: 'Buts Contre',
      goalsTimeline: 'Chronologie des Buts',
      goal: 'But'
    },
    
    // Share Dialog
    shareDialog: {
      title: 'Partager',
      description: 'Partagez cette analyse avec d\'autres utilisateurs. Ils devront être connectés pour la voir.',
      generateShareLink: 'Générer un lien de partage',
      done: 'Terminé',
      failedToCreateShare: 'Échec de la création du lien de partage',
      failedToCopyLink: 'Échec de la copie du lien'
    },

    // Upgrade Modal
    upgradeModal: {
      title: 'Passer à Pro',
      subtitle: 'Débloquez des matchs illimités et des analyses IA',
      whatYouGet: 'Ce que vous obtenez :',
      unlimitedGames: 'Suivi de matchs illimité',
      unlimitedAnalyses: 'Analyses IA illimitées',
      prioritySupport: 'Support prioritaire',
      guaranteeTitle: 'Garantie 30 jours satisfait ou remboursé',
      guaranteeText: 'Essayez Pro sans risque pendant 30 jours. Si vous n\'êtes pas 100% satisfait, obtenez un remboursement intégral — sans question.',
      confirmCta: 'Confirmer & Payer',
      cancelAnywhere: 'Annulable à tout moment — effet à l\'échéance annuelle',
      annualNotice: 'Facturation annuelle — annulable à tout moment'
    }
  }
} as const;

export type TranslationKey = string;
export type Language = 'en' | 'fr';